import { LicenseManager } from 'ag-grid-enterprise'
if (import.meta.env.VITE_AG_GRID_KEY) {
  LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_KEY)
}
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import ArcoVue from '@arco-design/web-vue'
import ArcoVueIcon from '@arco-design/web-vue/es/icon'
import App from './App.vue'
import router from './router'
import i18n from './i18n'
import { setupVueQuery } from '@/utils/query'
import { setupDirectives } from '@/directives'
import { setupPlugins } from '@/plugins'
import { initBaiduAnalytics, trackPv } from '@/utils/baidu-analytics'
import { initTracking, setupTabClickTracking } from '@/tracking'

// 处理Vite预加载错误（适用于Vite 4.4+）
window.addEventListener('vite:preloadError', event => {
  console.warn('检测到Vite预加载错误，即将刷新页面:', event)

  // 避免无限刷新循环
  const lastReload = sessionStorage.getItem('lastVitePreloadError')
  const now = Date.now()

  if (!lastReload || now - parseInt(lastReload) > 30000) { // 30秒内不重复刷新
    sessionStorage.setItem('lastVitePreloadError', now.toString())
    window.location.reload()
  } else {
    console.error('检测到频繁的Vite预加载错误，停止自动刷新')
  }
})

// FontAwesome 配置
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faShip,
  faTruck,
  faFileInvoice,
  faMoneyBillWave,
  faAnchor,
  faWarehouse,
  faFileAlt,
  faWeightHanging,
  faFileContract,
  faReceipt,
  faFileInvoiceDollar,
  faShippingFast,
  faClipboardList,
  faCheckCircle,
  // 认证页面需要的图标
  faUser,
  faLock,
  faPhone,
  faEnvelope,
  faEye,
  faEyeSlash,
  faUserCheck,
  faInfoCircle,
  faSignInAlt,
  faKey,
  faSpinner
} from '@fortawesome/free-solid-svg-icons'

// 添加图标到库
library.add(
  faShip,
  faTruck,
  faFileInvoice,
  faMoneyBillWave,
  faAnchor,
  faWarehouse,
  faFileAlt,
  faWeightHanging,
  faFileContract,
  faReceipt,
  faFileInvoiceDollar,
  faShippingFast,
  faClipboardList,
  faCheckCircle,
  // 认证页面图标
  faUser,
  faLock,
  faPhone,
  faEnvelope,
  faEye,
  faEyeSlash,
  faUserCheck,
  faInfoCircle,
  faSignInAlt,
  faKey,
  faSpinner
)

// 样式导入
import '@arco-design/web-vue/dist/arco.css'
import '@ai-chatbox/vue/dist/style.css'  // AI组件包样式
import './styles/tailwind.css'
import './styles/index.scss'

// 简单的环境变量检查
if (!import.meta.env.VITE_API_BASE_URL) {
  console.warn('[CT_ENV_WARNING] 缺少 VITE_API_BASE_URL 环境变量')
}

// 创建应用实例
const app = createApp(App)

// 创建 Pinia 实例
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

// 安装插件
app.use(ArcoVue)
app.use(ArcoVueIcon)
app.use(pinia)
app.use(router)
app.use(i18n)

// 注册FontAwesome组件
app.component('FontAwesomeIcon', FontAwesomeIcon)

// 初始化 TanStack Query
setupVueQuery(app)

// 注册自定义指令
setupDirectives(app)

// 注册插件（自动为所有元素添加测试ID和focusable属性）
setupPlugins(app, {
  autoTestId: {
    enabled: true,                              // 启用插件
    enableInProduction: true,                   // 生产环境也启用（便于自动化测试）
    verbose: process.env.NODE_ENV === 'development'  // 开发环境显示日志
  }
})

// 神策：init 须在 mount 前；PageView/按钮由 SDK 全埋点；tab 切换由 setupTabClickTracking 全局监听
initTracking()
setupTabClickTracking()

// 挂载应用
app.mount('#app')

// Baidu Tongji
initBaiduAnalytics(import.meta.env.VITE_BAIDU_HM_ID as any)
router.isReady().then(() => {
  trackPv(router.currentRoute.value.fullPath)
})
router.afterEach(to => {
  trackPv(to.fullPath)
})

// 开发环境下的调试信息
if (import.meta.env.DEV) {
  console.log('[CT_INIT] Control Tower 管理系统初始化完成')
  console.log('[CT_INIT] 环境:', import.meta.env.MODE)
  console.log('[CT_INIT] API地址:', import.meta.env.VITE_API_BASE_URL)
}
